import { useMemo } from "react";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { DatesProvider } from "../../dates/DatesContext";
import moment from "moment";
import BreadcrumbDates, { type Breadcrumb } from "../../components/core/blocks/BreadcrumbDates";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "../../utils/dashboardLinks";
import { useModuleEnabled } from "../../redux/selectors/modules";
import checkTierAccess from "../../utils/checkTierAccess";
import PaymentDiscovery from "../billing/PaymentDiscovery";
import EmptyState from "../../components/custom/merchandise/EmptyState";
import { useAppSelector } from "../../redux/hooks";
import { V4Metrics } from "../../modules/pricingv2/components/dashboard/V4Metrics";
import { PageContainer } from "../../components/core/basic/PageContainer";

const Dashboard = () => {
  const { t } = useTranslation(["pricingv2"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);

  const breadCrumbItems: Breadcrumb[] = [
    AGENCY_DASHBOARD_BREADCRUMB_SPEC,
    {
      name: t(($) => $.pricingv2["Pricing Dashboard"]),
      href: "/user/pricing/dashboard",
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: t(($) => $.pricingv2["Dashboards"]),
    },
  ];

  const showEmpty = useMemo(() => {
    const totalSalesStart = user.total_sales_start_date?.[marketPlace.marketPlace];
    if (totalSalesStart) {
      if (moment(totalSalesStart).isBefore(moment().subtract(2, "days"))) {
        return false;
      }
      return "COLLECTING_DATA";
    }
    return "NO_DATA";
  }, [user, marketPlace.marketPlace]);

  if (showEmpty) {
    return <EmptyState type={showEmpty} />;
  }

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} hideCompare hideGroupBy hideIncludeCurrentPeriod></BreadcrumbDates>
      <PageContainer withBreadcrumb padding={"large"}>
        <V4Metrics />
      </PageContainer>
    </>
  );
};

const PricingDashboardV4 = () => {
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");

  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);

  const location = useLocation();

  const DYNAMIC_PRICING_SUBSCRIPTION =
    (((user.pricing_tier && user.pricing_tier !== "unknown") || location.state.firstVisit) &&
      (amz_onboard.org_status === "active" ||
        amz_onboard.org_status === "trial_active" ||
        amz_onboard.org_status === "demo_org")) ||
    hasPriceOptimizationModule ||
    checkTierAccess(user.tier, "pricing", amz_onboard.org_status);

  if (!DYNAMIC_PRICING_SUBSCRIPTION) {
    return <PaymentDiscovery discoveryType={"advertising"} dashboardType="pricing" />;
  }

  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default PricingDashboardV4;
